import Card from "@material-ui/core/Card";
import { makeStyles, Theme } from "@material-ui/core/styles";
import { RefObject, useEffect, useRef, useState } from "react";
import {
    changeAppState,
    getAppState,
    useAppState,
} from "../../../../../../../../common/appState";
import { useScreenSize } from "../../../../../../../../common/hooks/useScreenSize";
import { fetchPostFormData } from "../../../../../../../../common/util/fetch";
import { SavingsIcon } from "../../../../../../../../icon/SavingsIcon";
import { FetchResult } from "../../../../../../../../types/fetch";
import ShurikenProgress from "../../../../../../../shared/Animations/ShurikenProgress";
import { SuccessButton } from "../../../../../../../shared/Button/SuccessButton";
import { addRegisteredUserXp } from "../../../../../../../shared/Dialog/ResultXpDialog/RegisteredUser";
import { openOtherUserPanel } from "../../../../../../../shared/User/OtherUserPanel/functions";
import { User } from "../../../../../../../shared/User/types";
import { UserAvatar } from "../../../../../../../shared/User/UserAvatar/UserAvatar";
import { InitialView } from "../../../MyPageTop/types";
import { OpenableCard } from "../OpenableCard";
import { InviteFriendsButton } from "./InviteFriendsButton";
import { InvitationBonus } from "./types";

const cardId = "MyPageBonusCard";

export function BonusCard({
    initialView,
    scrollableContainerRef,
}: {
    initialView?: InitialView;
    scrollableContainerRef: RefObject<HTMLDivElement>;
}) {
    const [open, setOpen] = useState(false);
    const [invitationBonusList] = useAppState("invitationBonusList");

    const c = useStyles();

    const validBonusCount = invitationBonusList.filter(
        b => b.coins > 0 || b.xp > 0
    ).length;

    return (
        <OpenableCard
            title={
                <span>
                    Bonus
                    {validBonusCount > 0 && (
                        <>
                            {": "}
                            <span className={c.titleNumber}>
                                {validBonusCount}
                            </span>
                        </>
                    )}
                </span>
            }
            icon={<SavingsIcon />}
            saveKey={cardId}
            id={cardId}
            open={open}
            setOpen={setOpen}
            alwaysShowIcon
            alwaysShowTitle
            initiallyOpenedId={initialView}
            exclamationMark={validBonusCount > 0}
            scrollableContainerRef={scrollableContainerRef}
        >
            <BonusList invitationBonusList={invitationBonusList} />
        </OpenableCard>
    );
}
const useStyles = makeStyles(theme => ({
    titleNumber: { color: theme.palette.success.dark },
}));

function BonusList({
    invitationBonusList,
}: {
    invitationBonusList: InvitationBonus[];
}) {
    const hasNoData = invitationBonusList.length === 0;
    const c = useListStyles({ hasNoData });
    const [submitting, setSubmitting] = useState(false);

    return (
        <div className={c.container}>
            <div className={c.explanation}>
                XP and Coins from users you have invited
            </div>

            <div className={c.recordsContainer}>
                {invitationBonusList.map((bonus, i) => (
                    <BonusRecord
                        key={bonus.fromUser.userId}
                        bonus={bonus}
                        i={i}
                        globalSubmitting={submitting}
                        setGlobalSubmitting={setSubmitting}
                    />
                ))}
            </div>

            <InviteFriendsButton hasNoData={hasNoData} />
        </div>
    );
}
const useListStyles = makeStyles<Theme, { hasNoData: boolean }>(theme => ({
    container: {
        backgroundColor: theme.palette.grey[100],
        height: 308,
        maxHeight: 308,
        flex: 1,
        fontSize: "large",
        overflowY: "auto",
        overflowX: "hidden",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
    },
    noData: {
        width: "100%",
        height: 305,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: theme.palette.grey[600],
    },
    explanation: ({ hasNoData }) => ({
        color: theme.palette.grey[700],
        fontSize: "small",
        paddingTop: hasNoData ? 95 : 5,
        paddingBottom: hasNoData ? 45 : 0,
    }),
    recordsContainer: {
        display: "flex",
        flexDirection: "column",
        gap: 5,
        margin: "5px 0",
        width: "100%",
    },
}));

type SubmittingTarget = false | "coin" | "xp";
function BonusRecord({
    bonus,
    i,
    globalSubmitting,
    setGlobalSubmitting,
}: {
    bonus: InvitationBonus;
    i: number;
    globalSubmitting: boolean;
    setGlobalSubmitting: (submitting: boolean) => void;
}) {
    const hasBonus = bonus.xp > 0 || bonus.coins > 0;
    const { usernameWidth, cardRef } = useUsernameWidth();
    const c = useRecordStyles({ usernameWidth, hasBonus });

    const [localSubmitting, setLocalSubmitting] =
        useState<SubmittingTarget>(false);
    const setSubmitting = (newSubmitting: SubmittingTarget) => {
        setLocalSubmitting(newSubmitting);
        setGlobalSubmitting(!!newSubmitting);
    };

    return (
        <Card className={c.card} ref={cardRef}>
            <div
                className={c.avatarAndNameContainer}
                onClick={() => {
                    openOtherUserPanel(bonus.fromUser.userId);
                }}
            >
                <UserAvatar user={bonus.fromUser} colorNumber={i} size={40} />
                <div className={c.username}>{bonus.fromUser.name}</div>
            </div>
            <div className={c.bothBonusRow}>
                <div className={c.eachBonusRow}>
                    <div className={c.bonusTypeAndNumber}>
                        <div>XP:</div>
                        <div
                            className={bonus.xp > 0 ? c.bonusNumber : undefined}
                        >
                            {bonus.xp.toLocaleString()}
                        </div>
                    </div>
                    <SuccessButton
                        className={c.receiveButton}
                        disabled={bonus.xp < 1 || globalSubmitting}
                        onClick={async () => {
                            const { user } = getAppState();
                            if (!user) {
                                return;
                            }
                            setSubmitting("xp");

                            // Update remaining bonus XP
                            const fetchResult = await fetchClaimBonusXp(
                                user.userId,
                                bonus.fromUser.userId,
                                bonus.xp
                            );
                            if (!fetchResult) {
                                alert("Failed! Something is wrong!");
                                setSubmitting(false);
                                return;
                            }

                            // Show dialog and update user's XP data
                            await addRegisteredUserXp(
                                {
                                    xpToAdd: bonus.xp,
                                    topSmallMessage: null,
                                    boostRate: 1,
                                },
                                user
                            );
                            setSubmitting(false);
                        }}
                    >
                        {localSubmitting === "xp" ? (
                            <ShurikenProgress size={20} />
                        ) : (
                            "Receive"
                        )}
                    </SuccessButton>
                </div>

                <div className={c.eachBonusRow}>
                    <div className={c.bonusTypeAndNumber}>
                        <div>Coins:</div>
                        <div
                            className={
                                bonus.coins > 0 ? c.bonusNumber : undefined
                            }
                        >
                            {bonus.coins.toLocaleString()}
                        </div>
                    </div>
                    <SuccessButton
                        className={c.receiveButton}
                        disabled={bonus.coins < 1 || globalSubmitting}
                        onClick={async () => {
                            const { user } = getAppState();
                            if (!user) {
                                return;
                            }
                            setSubmitting("coin");

                            // Update remaining bonus coins and user's coin data
                            const fetchResult = await fetchClaimBonusCoins(
                                user.userId,
                                bonus.fromUser.userId,
                                bonus.coins
                            );
                            if (!fetchResult) {
                                alert("Failed! Something is wrong!");
                                setSubmitting(false);
                                return;
                            }

                            // Show dialog
                            changeAppState("addCoinsDialogState", {
                                open: true,
                                title: "You got coins!",
                                coins: bonus.coins,
                            });
                            setSubmitting(false);
                        }}
                    >
                        {localSubmitting === "coin" ? (
                            <ShurikenProgress size={20} />
                        ) : (
                            "Receive"
                        )}
                    </SuccessButton>
                </div>
            </div>
        </Card>
    );
}
const recordCardPadding = 5;
const useRecordStyles = makeStyles<
    Theme,
    { usernameWidth: number; hasBonus: boolean }
>(theme => ({
    card: ({ hasBonus }) => ({
        margin: "0 5px",
        display: "flex",
        padding: recordCardPadding,
        height: 75,
        justifyContent: "space-between",
        width: "calc(100% - 10px)",
        position: "relative",
        overflow: "visible",
        border: hasBonus
            ? `solid 3px ${theme.palette.success.main}`
            : undefined,
    }),
    avatarAndNameContainer: {
        display: "flex",
        alignItems: "center",
        gap: 10,
        marginLeft: 5,
        cursor: "pointer",
    },
    username: ({ usernameWidth }) => ({
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        maxWidth: usernameWidth,
        transition: "all 500ms",
    }),
    bothBonusRow: {
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        gap: 5,
    },
    eachBonusRow: {
        height: 30,
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: 15,
    },
    bonusTypeAndNumber: {
        display: "flex",
        alignItems: "baseline",
        justifyContent: "center",
        gap: 5,
    },
    bonusNumber: {
        fontWeight: "bold",
        color: theme.palette.success.main,
    },
    receiveButton: {
        maxHeight: 27,
        minHeight: 27,
        fontSize: "15px !important",
        lineHeight: 1,
        width: 90,
    },
}));

function useUsernameWidth() {
    const cardRef = useRef<HTMLDivElement>(null);
    const [usernameWidth, setUsernameWidth] = useState(300);
    const { screenWidth, screenHeight } = useScreenSize();
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            const cardEl = cardRef.current;
            if (!cardEl) {
                return;
            }
            const dif = cardEl.scrollWidth - cardEl.clientWidth;
            if (dif > 0) {
                setUsernameWidth(usernameWidth - dif - 2 * recordCardPadding);
            }
        }, 500);
        return () => {
            clearTimeout(timeoutId);
        };
    }, [screenWidth, screenHeight, usernameWidth]);
    return { usernameWidth, cardRef };
}

async function fetchClaimBonusCoins(
    userId: number,
    invitedGuestUserId: number,
    claimedBonusCoins: number
) {
    const formData = new FormData();
    formData.append("userId", userId.toString());
    formData.append("invitedGuestUserId", invitedGuestUserId.toString());
    formData.append("claimedBonusCoins", claimedBonusCoins.toString());

    const result = await fetchPostFormData<
        FetchResult<{ invitationBonusList: InvitationBonus[]; user: User }>
    >("api/InvitationCard/ClaimBonusCoins", formData);
    if ("error" in result) {
        return false;
    }
    changeAppState("invitationBonusList", result.invitationBonusList);
    changeAppState("user", result.user);
    return true;
}

async function fetchClaimBonusXp(
    userId: number,
    invitedGuestUserId: number,
    claimedBonusXp: number
) {
    const formData = new FormData();
    formData.append("userId", userId.toString());
    formData.append("invitedGuestUserId", invitedGuestUserId.toString());
    formData.append("claimedBonusXp", claimedBonusXp.toString());

    const result = await fetchPostFormData<
        FetchResult<{ invitationBonusList: InvitationBonus[] }>
    >("api/InvitationCard/ClaimBonusXp", formData);
    if ("error" in result) {
        return false;
    }
    changeAppState("invitationBonusList", result.invitationBonusList);
    return true;
}
